import { View, Text, ScrollView } from 'react-native'
import React from 'react'
import { StyleSheet } from 'react-native-unistyles'
import Unicon from '@/components/General/Unicon'
import CutCard from '@/components/General/CutCard'
import { useRouter } from 'expo-router'
import { Query } from '@/utils/react-query/Queries'
import usePlatform from '@/utils/hooks/usePlatform'
import useBibleStudies from '@/utils/hooks/useBibleStudies'

function DashboardCard({ title, description, icon, iconLibrary = 'ionicon', onPress, testID }) {
    return (
        <CutCard onPress={onPress} testID={testID} accentColor="primary" style={styles.card}>
            <View style={styles.cardHeader}>
                <View style={styles.iconContainer}>
                    <Unicon name={icon} library={iconLibrary} colorVariant="onPrimary" size={20} />
                </View>
                <View style={styles.cardTitleRow}>
                    <Text style={styles.cardTitle}>{title}</Text>
                    <Unicon name="chevron-forward" colorVariant="textSecondary" size={16} />
                </View>
            </View>
            <Text style={styles.cardSubTitle}>{description}</Text>
        </CutCard>
    )
}

function SectionHeader({ title, icon, iconLibrary = 'ionicon' }) {
    return (
        <View style={styles.sectionHeader}>
            <Unicon name={icon} library={iconLibrary} colorVariant="primary" size={18} />
            <Text style={styles.sectionTitle}>{title}</Text>
        </View>
    )
}

export default function index() {
    const router = useRouter();
    const { isWeb } = usePlatform();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: smallGroupTypes } = Query.smallGroupTypes.all();
    const { enabled: bibleStudiesEnabled, canViewReports: canViewBibleStudyReports } = useBibleStudies();

    return (
        <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer} keyboardShouldPersistTaps="handled"
>
            <View style={styles.hero}>
                <View style={styles.heroIconContainer}>
                    <Unicon name="bar-chart" library="ionicon" colorVariant="onPrimary" size={28} />
                </View>
                <Text style={styles.heroTitle}>Reports & Analytics</Text>
                <Text style={styles.heroDescription}>
                    Explore dashboards and reports to gain insights into attendance, giving, small groups, and more.
                </Text>
            </View>

            {(loggedInUser?.can?.("Check In") || loggedInUser?.can?.("View Contributions") || loggedInUser?.isAdmin) &&
                <SectionHeader title="Dashboards" icon="speedometer-outline" />
            }

            <View style={isWeb ? styles.grid : styles.stack}>
                {loggedInUser?.can?.("Check In") &&
                    <DashboardCard
                        title="Attendance Dashboard"
                        description="Track attendance trends, event totals, and visitor patterns."
                        icon="calendar-outline"
                        onPress={() => router.push("/analytics/dashboards/attendance")}
                    />
                }

                {loggedInUser?.isAdmin &&
                    <DashboardCard
                        title="Volunteer Utilization"
                        description="See who is serving, their positions, event coverage, and response trends."
                        icon="people-outline"
                        testID="volunteer-utilization-card"
                        onPress={() => router.push("/analytics/dashboards/volunteer-utilization")}
                    />
                }

                {loggedInUser?.can?.("View Contributions") &&
                    <DashboardCard
                        title="Contributions Dashboard"
                        description="Monitor giving trends, giver activity, and fund totals."
                        icon="cash-outline"
                        onPress={() => router.push("/analytics/dashboards/contributions")}
                    />
                }

                {loggedInUser?.can?.("View Contributions") &&
                    <DashboardCard
                        title="Online Giving Reports"
                        description="Review activity and processing details for online gifts."
                        icon="bar-chart-outline"
                        testID="online-giving-reports-card"
                        onPress={() => router.push("/analytics/dashboards/onlinegiving")}
                    />
                }
            </View>

            {loggedInUser?.can?.("View People") &&
                <SectionHeader title="People Reports" icon="people-outline" />
            }

            <View style={isWeb ? styles.grid : styles.stack}>
                {(loggedInUser?.can?.("View People") && loggedInUser?.can?.("Check In")) &&
                    <DashboardCard
                        title="Missed Report"
                        description="See people who haven't attended an event recently."
                        icon="person-remove-outline"
                        onPress={() => router.push("/analytics/reports/missed")}
                    />
                }

                {(loggedInUser?.can?.("View People")) &&
                    <DashboardCard
                        title="Upcoming Birthdays"
                        description="Celebrate members with birthdays coming up soon."
                        icon="gift-outline"
                        onPress={() => router.push("/analytics/reports/birthdays")}
                    />
                }
            </View>

            {loggedInUser?.can?.("View Small Groups") && (
                <>
                    <SectionHeader title="Small Groups" icon="people-circle-outline" />
                    <View style={isWeb ? styles.grid : styles.stack}>
                        {smallGroupTypes?.filter(type => !bibleStudiesEnabled || type.purpose !== 'BIBLE_STUDY').map((type) => (
                            <DashboardCard
                                key={type.id}
                                title={`${type.name} Analytics`}
                                description={`View participation, leader effectiveness, and trends for ${type.name}.`}
                                icon="git-branch-outline"
                                onPress={() => router.push({
                                    pathname: "/analytics/dashboards/smallgroups",
                                    params: { typeID: type.id }
                                })}
                            />
                        ))}
                    </View>
                </>
            )}

            {bibleStudiesEnabled && canViewBibleStudyReports && (
                <>
                    <SectionHeader title="Bible Studies" icon="book-outline" />
                    <View style={isWeb ? styles.grid : styles.stack}>
                        <DashboardCard
                            title="Bible Study Reports"
                            description="Track teaching activity, outcomes, and teacher engagement."
                            icon="bar-chart-outline"
                            testID="bible-study-reports-card"
                            onPress={() => router.push("/biblestudies/reports")}
                        />
                    </View>
                </>
            )}
        </ScrollView>
    )
}

const styles = StyleSheet.create(({ colors, fonts }) => ({
    container: {
        flex: 1,
        backgroundColor: 'transparent',
    },
    contentContainer: {
        padding: 20,
        gap: 20,
        _web: {
            maxWidth: 1200,
            alignSelf: 'center',
            width: '100%',
        }
    },
    hero: {
        alignItems: 'center',
        paddingVertical: 20,
        gap: 12,
    },
    heroIconContainer: {
        width: 56,
        height: 56,
        borderRadius: 16,
        backgroundColor: colors.primary,
        alignItems: 'center',
        justifyContent: 'center',
    },
    heroTitle: {
        fontSize: 24,
        fontFamily: fonts.heading,
        color: colors.text,
        textAlign: 'center',
    },
    heroDescription: {
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textAlign: 'center',
        maxWidth: 500,
        lineHeight: 20,
    },
    sectionHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginTop: 8,
    },
    sectionTitle: {
        fontSize: 16,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    grid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 15,
    },
    stack: {
        flexDirection: 'column',
        gap: 12,
    },
    card: {
        padding: 16,
        _web: {
            flex: 1,
            minWidth: 280,
            maxWidth: 'calc(33.333% - 10px)',
            // Hover lift promotes a card to its own layer, repainting the last
            // card's corner accents. Promote all cards upfront so nothing shifts.
            willChange: 'transform',
        }
    },
    cardHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        marginBottom: 8,
    },
    iconContainer: {
        width: 36,
        height: 36,
        borderRadius: 10,
        backgroundColor: colors.primary,
        alignItems: 'center',
        justifyContent: 'center',
    },
    cardTitleRow: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    cardTitle: {
        fontSize: 15,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    cardSubTitle: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        lineHeight: 18,
        marginLeft: 48,
    }
}))
